import React, { useEffect, useMemo, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import api from "../../api";
import Can from "../../components/Can";
import "./AdminStaffPage.css";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";
import StaffImportModal from "./StaffImportModal";
import { getSerialNumber } from "../../utils/pagination";
import {
  belongsToUnit,
  fetchAllPages,
  filterDepartmentsByUnit,
  filterDesignations,
  filterReportingManagers,
} from "../../utils/orgOptions";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";

const initialForm = {
  name: "",
  username: "",
  email: "",
  phone_no: "",
  password: "",
  department: "",
  role_id: "",
  unit_id: "",
  company_id: "",
  // Phase 1A: configurable organization-structure fields. All 4 are
  // optional/additive — existing staff records without them keep working
  // unchanged; see PHASE_1A_IMPLEMENTATION_LOG.md.
  employee_code: "",
  department_id: "",
  designation_id: "",
  reporting_manager_id: "",
};

// Admin may keep (and re-pick) a saved value outside the unit filter, so an
// existing assignment never disappears from the dropdown.
const keepSaved = (all, filtered, savedId) => {
  if (!savedId || filtered.some((x) => String(x.id) === String(savedId))) return filtered;
  const saved = all.find((x) => String(x.id) === String(savedId));
  return saved ? [...filtered, saved] : filtered;
};

const AdminStaffPage = () => {
  const [staffList, setStaffList] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(initialForm);

  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Quick Activate/Deactivate confirmation (Actions column) -- separate
  // from the Edit Staff modal's own Account Status toggle.
  const [statusTarget, setStatusTarget] = useState(null);
  const [settingStatus, setSettingStatus] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [unitFilter, setUnitFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  const [units, setUnits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    fetchStaff();
    fetchDropdownData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, unitFilter, roleFilter, companyFilter, departmentFilter]);

  const fetchDropdownData = async () => {
    try {
      const [unitRes, roleRes, companyRes, deptRows, desRows] = await Promise.all([
        api.get(`/units`),
        api.get(`/roles`),
        api.get(`/companies`),
        // Phase 1A: only ACTIVE departments/designations are offered for
        // new assignment — inactive ones stay visible on existing users
        // (via *_name from the backend join) but are not selectable here.
        // All pages: the list APIs cap pageSize at 200.
        fetchAllPages(api, `/departments`, { is_active: 1 }),
        fetchAllPages(api, `/designations`, { is_active: 1 }),
      ]);

      setUnits(Array.isArray(unitRes.data) ? unitRes.data : []);
      setRoles(Array.isArray(roleRes.data) ? roleRes.data : []);

      const companyData = companyRes.data?.data || companyRes.data || [];
      setCompanies(Array.isArray(companyData) ? companyData : []);

      setDepartments(deptRows);
      setDesignations(desRows);
    } catch (err) {
      console.error("Dropdown Fetch Error:", err);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/users`);
      setStaffList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch staff", err);
      toast.error("Failed to fetch staff");
    } finally {
      setLoading(false);
    }
  };

  const roleMap = useMemo(() => {
    return Object.fromEntries(roles.map((r) => [String(r.id), r.name]));
  }, [roles]);

  const unitMap = useMemo(() => {
    return Object.fromEntries(units.map((u) => [String(u.id), u.name]));
  }, [units]);

  const companyMap = useMemo(() => {
    return Object.fromEntries(
      companies.map((c) => [String(c.id), c.company_name])
    );
  }, [companies]);

  const departmentMap = useMemo(() => {
    return Object.fromEntries(departments.map((d) => [String(d.id), d.name]));
  }, [departments]);

  const designationMap = useMemo(() => {
    return Object.fromEntries(designations.map((d) => [String(d.id), d.name]));
  }, [designations]);

  // Phase 1A: designation options are filtered to the selected department
  // when that designation is department-specific; department-agnostic
  // designations (department_id === null) remain available regardless.
  // Unit-wise: departments belonging to the selected unit plus unit-agnostic
  // ones (unit_id === null). The currently saved value is always kept so an
  // existing assignment never disappears from the dropdown.
  const unitDepartments = useMemo(
    () => filterDepartmentsByUnit(departments, form.unit_id),
    [departments, form.unit_id]
  );

  const departmentOptions = useMemo(
    () => keepSaved(departments, unitDepartments, form.department_id),
    [departments, unitDepartments, form.department_id]
  );

  // No department picked: limit to designations of the unit's departments.
  const designationOptions = useMemo(() => {
    const allowed = form.unit_id ? new Set(unitDepartments.map((d) => String(d.id))) : null;
    return keepSaved(
      designations,
      filterDesignations(designations, form.department_id, allowed),
      form.designation_id
    );
  }, [designations, unitDepartments, form.department_id, form.designation_id, form.unit_id]);

  // Phase 1A: a user cannot be their own reporting manager.
  // Unit-wise: only active staff of the selected unit are offered.
  const reportingManagerOptions = useMemo(() => {
    const filtered = filterReportingManagers(staffList, { unitId: form.unit_id, excludeId: editingId });
    return String(form.reporting_manager_id) === String(editingId)
      ? filtered
      : keepSaved(staffList, filtered, form.reporting_manager_id);
  }, [staffList, editingId, form.unit_id, form.reporting_manager_id]);

  // Changing the unit drops department/designation/manager picks that no
  // longer belong to the new unit.
  const handleUnitChange = (unitId) => {
    setForm((prev) => {
      const next = { ...prev, unit_id: unitId };
      const dept = departments.find((d) => String(d.id) === String(prev.department_id));
      if (dept && !belongsToUnit(dept.unit_id, unitId)) {
        next.department_id = "";
        next.designation_id = "";
      }
      const mgr = staffList.find((s) => String(s.id) === String(prev.reporting_manager_id));
      if (mgr && unitId && String(mgr.unit_id) !== String(unitId)) {
        next.reporting_manager_id = "";
      }
      return next;
    });
  };

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);

    let data = [...staffList];

    if (tokens.length > 0) {
      data = data.filter((staff) => {
        const roleName = roleMap[String(staff.role_id)] || "";
        const unitName = staff.unit_name || unitMap[String(staff.unit_id)] || "";
        const companyName =
          staff.company_name || companyMap[String(staff.company_id)] || "";

        const haystack = [
          staff.id,
          staff.name,
          staff.username,
          staff.email,
          staff.phone_no,
          staff.department,
          staff.employee_code,
          staff.department_name,
          staff.designation_name,
          staff.reporting_manager_name,
          staff.role_id,
          roleName,
          staff.unit_id,
          unitName,
          staff.company_id,
          companyName,
        ]
          .map((v) => (v ?? "").toString().toLowerCase())
          .join(" ");

        return tokens.every((token) => haystack.includes(token));
      });
    }

    if (unitFilter) {
      data = data.filter((staff) => String(staff.unit_id) === unitFilter);
    }

    if (roleFilter) {
      data = data.filter((staff) => String(staff.role_id) === roleFilter);
    }

    if (companyFilter) {
      data = data.filter((staff) => String(staff.company_id) === companyFilter);
    }

    if (departmentFilter) {
      data = data.filter(
        (staff) => String(staff.department_id) === departmentFilter
      );
    }

    data.sort((a, b) => {
      let valueA = "";
      let valueB = "";

      if (sortField === "role") {
        valueA = roleMap[String(a.role_id)] || "";
        valueB = roleMap[String(b.role_id)] || "";
      } else if (sortField === "unit") {
        valueA = a.unit_name || unitMap[String(a.unit_id)] || "";
        valueB = b.unit_name || unitMap[String(b.unit_id)] || "";
      } else if (sortField === "company") {
        valueA = a.company_name || companyMap[String(a.company_id)] || "";
        valueB = b.company_name || companyMap[String(b.company_id)] || "";
      } else {
        valueA = a[sortField] ?? "";
        valueB = b[sortField] ?? "";
      }

      valueA = valueA.toString().toLowerCase();
      valueB = valueB.toString().toLowerCase();

      if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
      if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return data;
  }, [
    staffList,
    search,
    roleMap,
    unitMap,
    companyMap,
    sortField,
    sortOrder,
    unitFilter,
    roleFilter,
    companyFilter,
    departmentFilter,
  ]);

  const hasActiveFilters =
    !!search || !!unitFilter || !!roleFilter || !!companyFilter || !!departmentFilter;

  const clearFilters = () => {
    setSearch("");
    setUnitFilter("");
    setRoleFilter("");
    setCompanyFilter("");
    setDepartmentFilter("");
  };

  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredStaff.slice(indexOfFirst, indexOfLast);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    const isActive = sortField === field;
    const iconClass = isActive
      ? sortOrder === "asc"
        ? "fa-arrow-up"
        : "fa-arrow-down"
      : "fa-sort";

    return (
      <i
        className={`fa-solid ${iconClass} staff-sort-icon${isActive ? " staff-sort-icon-active" : ""}`}
        aria-hidden="true"
      ></i>
    );
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openAdd = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEdit = (staff) => {
    setForm({
      name: staff.name || "",
      username: staff.username || "",
      email: staff.email || "",
      phone_no: staff.phone_no || "",
      password: "",
      department: staff.department || "",
      role_id: staff.role_id || "",
      unit_id: staff.unit_id || "",
      company_id: staff.company_id || "",
      employee_code: staff.employee_code || "",
      department_id: staff.department_id || "",
      designation_id: staff.designation_id || "",
      reporting_manager_id: staff.reporting_manager_id || "",
      // Edit-only -- maps directly to users.is_active (1=Active, 0=Inactive).
      // Not part of the create flow, so it's left out of `initialForm`.
      is_active: staff.is_active ? 1 : 0,
    });

    setEditingId(staff.id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.warning("Name is required");
      return false;
    }

    if (!form.username.trim()) {
      toast.warning("Username is required");
      return false;
    }

    if (!form.email.trim()) {
      toast.warning("Email is required");
      return false;
    }

    if (!editingId && !form.password.trim()) {
      toast.warning("Password is required");
      return false;
    }

    if (!form.role_id) {
      toast.warning("Please select role");
      return false;
    }

    if (!form.unit_id) {
      toast.warning("Please select unit");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        name: form.name.trim(),
        username: form.username.trim(),
        email: form.email.trim(),
        phone_no: form.phone_no.trim(),
        department: form.department.trim(),
        role_id: Number(form.role_id),
        unit_id: Number(form.unit_id),
        company_id: form.company_id ? Number(form.company_id) : null,
        // Phase 1A: sent as null (not omitted) when cleared, so the backend
        // can tell "explicitly cleared" apart from "field not sent" — see
        // services/userOrgAssignmentService.js.
        employee_code: form.employee_code.trim() || null,
        department_id: form.department_id ? Number(form.department_id) : null,
        designation_id: form.designation_id ? Number(form.designation_id) : null,
        reporting_manager_id: form.reporting_manager_id ? Number(form.reporting_manager_id) : null,
      };

      if (!editingId) {
        payload.password = form.password;
      } else {
        // Account Status toggle -- users.is_active, edit-only (see openEdit).
        payload.is_active = form.is_active ? 1 : 0;
      }

      if (editingId) {
        await api.put(`/users/${editingId}`, payload);
        toast.success("Staff updated successfully");
      } else {
        await api.post(`/users`, payload);
        toast.success("Staff added successfully");
      }

      closeModal();
      fetchStaff();
    } catch (err) {
      console.error("Save failed", err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to save staff");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Staff deleted successfully");
      closeDeleteModal();
      fetchStaff();
    } catch (err) {
      console.error("Delete failed", err);
      toast.error(err.response?.data?.message || "Failed to delete staff");
    } finally {
      setDeleting(false);
    }
  };

  // Quick Activate/Deactivate action -- same PUT /users/:id endpoint the
  // Edit Staff modal's Account Status toggle uses, so both paths write the
  // same users.is_active column through the one existing update endpoint.
  const openStatusModal = (staff) => setStatusTarget(staff);
  const closeStatusModal = () => setStatusTarget(null);

  const confirmToggleStatus = async () => {
    if (!statusTarget) return;
    setSettingStatus(true);
    try {
      const nextActive = statusTarget.is_active ? 0 : 1;
      await api.put(`/users/${statusTarget.id}`, { is_active: nextActive });
      toast.success(nextActive ? "User activated" : "User deactivated");
      closeStatusModal();
      fetchStaff();
    } catch (err) {
      console.error("Toggle status failed", err);
      toast.error(err.response?.data?.message || "Failed to update user status");
    } finally {
      setSettingStatus(false);
    }
  };

  const exportCSV = () => {
    if (!filteredStaff.length) {
      toast.warning("No data available to export");
      return;
    }

    const headers = [
      "ID",
      "Name",
      "Username",
      "Email",
      "Phone",
      "Department",
      "Role",
      "Unit",
      "Company",
    ];

    const rows = filteredStaff.map((staff) => [
      staff.id,
      staff.name,
      staff.username,
      staff.email,
      staff.phone_no,
      staff.department,
      roleMap[String(staff.role_id)] || staff.role_id,
      staff.unit_name || unitMap[String(staff.unit_id)] || staff.unit_id,
      staff.company_name ||
        companyMap[String(staff.company_id)] ||
        staff.company_id ||
        "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value || "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "staff-list.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="unit-page admin-tasks-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Configuration</div>
          <h2>Staff Management</h2>
          <p>
            Manage staff users, departments, roles, units and company
            assignments.
          </p>
        </div>

        <div className="unit-header-actions">
          <button className="unit-btn unit-btn-light" onClick={fetchStaff}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </button>

          <button className="unit-btn unit-btn-light" onClick={exportCSV}>
            Export CSV
          </button>

          <Can moduleKey="users" actionKey="create">
            <button
              className="unit-btn unit-btn-light"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fa-solid fa-file-import" aria-hidden="true"></i>
              Import Excel
            </button>
          </Can>

          <Can moduleKey="users" actionKey="create">
            <button className="unit-btn unit-btn-primary" onClick={openAdd}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add Staff
            </button>
          </Can>
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="staff-stat-icon staff-stat-icon-primary">
            <i className="fa-solid fa-users" aria-hidden="true"></i>
          </div>
          <div className="staff-stat-text">
            <span>Total Staff</span>
            <strong>{staffList.length}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="staff-stat-icon staff-stat-icon-accent">
            <i className="fa-solid fa-filter" aria-hidden="true"></i>
          </div>
          <div className="staff-stat-text">
            <span>Filtered Result</span>
            <strong>{filteredStaff.length}</strong>
          </div>
        </div>

        <div className="unit-stat-card">
          <div className="staff-stat-icon staff-stat-icon-success">
            <i className="fa-solid fa-file-lines" aria-hidden="true"></i>
          </div>
          <div className="staff-stat-text">
            <span>Current Page</span>
            <strong>{currentPage}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-id-card staff-list-icon" aria-hidden="true"></i>
              Staff List
            </h5>
            <p>View, search, sort, add, update and delete staff records.</p>
          </div>

          <div className="unit-toolbar-actions">
            <div className="input-group unit-search">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </span>

              <input
                className="form-control"
                placeholder="Search name, email, phone, role, unit, company..."
                aria-label="Search name, email, phone, role, unit, company"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  className="unit-clear-btn"
                  type="button"
                  onClick={() => setSearch("")}
                >
                  Clear
                </button>
              )}
            </div>

            <select
              className="form-select staff-filter staff-filter-unit"
              aria-label="Filter by unit"
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
            >
              <option value="">All Units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>

            <select
              className="form-select staff-filter staff-filter-role"
              aria-label="Filter by role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>

            <select
              className="form-select staff-filter staff-filter-company"
              aria-label="Filter by company"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.company_name}
                </option>
              ))}
            </select>

            <select
              className="form-select staff-filter staff-filter-department"
              aria-label="Filter by department"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                className="unit-btn unit-btn-light"
                type="button"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            )}

            <select
              className="form-select unit-page-size staff-filter-rows"
              aria-label="Rows per page"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={5}>5 rows</option>
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </select>
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <div className="spinner-border" role="status"></div>
              <p>Loading staff...</p>
            </div>
          ) : (
            <table className="table unit-table">
              <thead>
                <tr>
                  <th style={{ width: "70px" }}>S.NO.</th>

                  <th className="sortable" onClick={() => handleSort("name")}>
                    <span className="staff-th-sort">
                      <span className="staff-th-label">Staff Name</span>
                      {getSortIcon("name")}
                    </span>
                  </th>

                  <th
                    className="sortable"
                    onClick={() => handleSort("username")}
                  >
                    <span className="staff-th-sort">
                      <span className="staff-th-label">Username</span>
                      {getSortIcon("username")}
                    </span>
                  </th>

                  <th className="sortable" onClick={() => handleSort("email")}>
                    <span className="staff-th-sort">
                      <span className="staff-th-label">Email</span>
                      {getSortIcon("email")}
                    </span>
                  </th>

                  <th>Phone</th>
                  <th>Employee Code</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>Reporting Manager</th>

                  <th className="sortable" onClick={() => handleSort("role")}>
                    <span className="staff-th-sort">
                      <span className="staff-th-label">Role</span>
                      {getSortIcon("role")}
                    </span>
                  </th>

                  <th className="sortable" onClick={() => handleSort("unit")}>
                    <span className="staff-th-sort">
                      <span className="staff-th-label">Unit</span>
                      {getSortIcon("unit")}
                    </span>
                  </th>

                  <th
                    className="sortable"
                    onClick={() => handleSort("company")}
                  >
                    <span className="staff-th-sort">
                      <span className="staff-th-label">Company</span>
                      {getSortIcon("company")}
                    </span>
                  </th>

                  <th>Status</th>

                  <th className="text-end" style={{ width: "230px" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((staff, index) => (
                    <tr key={staff.id}>
                      <td>{getSerialNumber(index, currentPage, itemsPerPage)}</td>

                      <td>
                        <div className="unit-name-box">
                          <div className="unit-letter">
                            {staff.name?.charAt(0)?.toUpperCase() || "S"}
                          </div>

                          <div>
                            <strong>{staff.name}</strong>
                            <small>Staff ID: {staff.id}</small>
                          </div>
                        </div>
                      </td>

                      <td>{staff.username || "-"}</td>
                      <td title={staff.email || ""}>{staff.email || "-"}</td>
                      <td>{staff.phone_no || "-"}</td>
                      <td>{staff.employee_code || "-"}</td>

                      <td>
                        {staff.department_name ||
                          departmentMap[String(staff.department_id)] || (
                            <span className="text-muted">Unmapped</span>
                          )}
                        {staff.department && (
                          <>
                            <br />
                            <small className="text-muted" title="Legacy free-text value — migration reference only">
                              {staff.department}
                            </small>
                          </>
                        )}
                      </td>

                      <td>
                        {staff.designation_name ||
                          designationMap[String(staff.designation_id)] ||
                          "-"}
                      </td>

                      <td>{staff.reporting_manager_name || "-"}</td>

                      <td>
                        <span className="unit-status">
                          {staff.role_name ||
                            roleMap[String(staff.role_id)] ||
                            staff.role_id ||
                            "-"}
                        </span>
                      </td>

                      <td>
                        {staff.unit_name ||
                          unitMap[String(staff.unit_id)] ||
                          staff.unit_id ||
                          "-"}
                      </td>

                      <td title={staff.company_name || companyMap[String(staff.company_id)] || ""}>
                        {staff.company_name ||
                          companyMap[String(staff.company_id)] ||
                          "-"}
                      </td>

                      <td>
                        <span className={`unit-status ${staff.is_active ? "" : "unit-status-inactive"}`}>
                          {staff.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td>
                        <div className="unit-actions">
                          <Can moduleKey="users" actionKey="edit">
                            <button
                              className="unit-icon-btn unit-edit staff-action-edit"
                              title="Edit Staff"
                              aria-label={`Edit ${staff.name || "staff"}`}
                              onClick={() => openEdit(staff)}
                            >
                              <i className="fa-solid fa-pen" aria-hidden="true"></i>
                            </button>
                          </Can>

                          <Can moduleKey="users" actionKey="edit">
                            <button
                              className={`unit-icon-btn staff-action-toggle${staff.is_active ? " staff-action-toggle--deactivate" : " staff-action-toggle--activate"}`}
                              title={staff.is_active ? "Deactivate Staff" : "Activate Staff"}
                              aria-label={staff.is_active ? `Deactivate ${staff.name || "user"}` : `Activate ${staff.name || "user"}`}
                              onClick={() => openStatusModal(staff)}
                            >
                              <i className={`fa-solid ${staff.is_active ? "fa-pause" : "fa-play"}`} aria-hidden="true"></i>
                            </button>
                          </Can>

                          <Can moduleKey="users" actionKey="delete">
                            <button
                              className="unit-icon-btn unit-delete staff-action-delete"
                              title="Delete Staff"
                              aria-label={`Delete ${staff.name || "staff"}`}
                              onClick={() => openDeleteModal(staff.id)}
                            >
                              <i className="fa-solid fa-trash" aria-hidden="true"></i>
                            </button>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="14">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-users" aria-hidden="true"></i>
                        </div>
                        <h5>No staff found</h5>
                        <p>Try another keyword or add a new staff member.</p>

                        <button
                          className="unit-btn unit-btn-primary"
                          onClick={openAdd}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          Add Staff
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing{" "}
            <strong>
              {filteredStaff.length === 0 ? 0 : indexOfFirst + 1}
            </strong>{" "}
            to <strong>{Math.min(indexOfLast, filteredStaff.length)}</strong>{" "}
            of <strong>{filteredStaff.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <ul className="pagination unit-pagination">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
              </li>

              {compactPageItems([...Array(totalPages)].map((_, idx) => (
                <li
                  key={idx}
                  className={`page-item ${
                    currentPage === idx + 1 ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(idx + 1)}
                  >
                    {idx + 1}
                  </button>
                </li>
              )), currentPage)}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      {showModal && (
        <div
          className="modal show fade d-block unit-modal"
          tabIndex="-1"
          style={{ background: "rgba(74, 14, 34, 0.55)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered admin-teal-modal admin-teal-modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  <PersonAddAltOutlinedIcon className="modal-header-icon" aria-hidden="true" />
                  {editingId ? "Edit Staff" : "Add New Staff"}
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  title="Close"
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input
                      className="form-control"
                      placeholder="Enter full name"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Username</label>
                    <input
                      className="form-control"
                      placeholder="Enter username"
                      value={form.username}
                      onChange={(e) => handleChange("username", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      className="form-control"
                      placeholder="Enter email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input
                      className="form-control"
                      placeholder="Enter phone number"
                      value={form.phone_no}
                      onChange={(e) => handleChange("phone_no", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Legacy Department <small className="text-muted">(migration reference only)</small>
                    </label>
                    <input
                      className="form-control"
                      placeholder="Old free-text department value"
                      value={form.department}
                      onChange={(e) =>
                        handleChange("department", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Employee Code</label>
                    <input
                      className="form-control"
                      placeholder="Enter employee code (optional)"
                      value={form.employee_code}
                      onChange={(e) => handleChange("employee_code", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Unit</label>
                    <select
                      className="form-select"
                      value={form.unit_id}
                      onChange={(e) => handleUnitChange(e.target.value)}
                    >
                      <option value="">Select Unit</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Department</label>
                    <select
                      className="form-select"
                      value={form.department_id}
                      onChange={(e) => {
                        handleChange("department_id", e.target.value);
                        // Selecting a new department may invalidate a
                        // designation that was specific to the old one.
                        handleChange("designation_id", "");
                      }}
                    >
                      <option value="">Select Department (optional)</option>
                      {departmentOptions.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Designation</label>
                    <select
                      className="form-select"
                      value={form.designation_id}
                      onChange={(e) => handleChange("designation_id", e.target.value)}
                    >
                      <option value="">Select Designation (optional)</option>
                      {designationOptions.map((des) => (
                        <option key={des.id} value={des.id}>
                          {des.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Reporting Manager</label>
                    <select
                      className="form-select"
                      value={form.reporting_manager_id}
                      onChange={(e) => handleChange("reporting_manager_id", e.target.value)}
                    >
                      <option value="">No reporting manager (optional)</option>
                      {reportingManagerOptions.map((mgr) => (
                        <option key={mgr.id} value={mgr.id}>
                          {mgr.name} {mgr.username ? `(${mgr.username})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={form.role_id}
                      onChange={(e) => handleChange("role_id", e.target.value)}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Company</label>
                    <select
                      className="form-select"
                      value={form.company_id}
                      onChange={(e) =>
                        handleChange("company_id", e.target.value)
                      }
                    >
                      <option value="">Select Company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.company_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {!editingId && (
                    <div className="col-md-6">
                      <label className="form-label">Password</label>
                      <input
                        className="form-control"
                        type="password"
                        placeholder="Enter password"
                        value={form.password}
                        onChange={(e) =>
                          handleChange("password", e.target.value)
                        }
                      />
                    </div>
                  )}

                  {editingId && (
                    <div className="col-md-6">
                      <label className="form-label d-block">Account Status</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="staffAccountStatusToggle"
                          checked={!!form.is_active}
                          onChange={(e) => handleChange("is_active", e.target.checked ? 1 : 0)}
                        />
                        <label className="form-check-label" htmlFor="staffAccountStatusToggle">
                          {form.is_active ? "Active" : "Inactive"}
                        </label>
                      </div>
                      <small className="text-muted">
                        Inactive users remain in historical records but should not be available for new assignments where active users are required.
                      </small>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="unit-btn unit-btn-light" onClick={closeModal}>
                  Cancel
                </button>

                <button
                  className="unit-btn unit-btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingId
                    ? "Update Staff"
                    : "Create Staff"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <StaffImportModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={fetchStaff}
      />

      <ConfirmModal
        show={showDeleteModal}
        onHide={closeDeleteModal}
        onConfirm={confirmDelete}
        busy={deleting}
        title="Delete Staff?"
        message="Are you sure you want to delete this staff member? This action cannot be undone."
        confirmLabel="Delete"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
        icon={<DeleteOutlineIcon fontSize="inherit" />}
      />

      <ConfirmModal
        show={!!statusTarget}
        onHide={closeStatusModal}
        onConfirm={confirmToggleStatus}
        busy={settingStatus}
        title={statusTarget?.is_active ? "Deactivate User?" : "Activate User?"}
        message={
          statusTarget?.is_active
            ? "Are you sure you want to deactivate this user?"
            : "Activate this user?"
        }
        confirmLabel={statusTarget?.is_active ? "Deactivate" : "Activate"}
        dialogClassName={
          statusTarget?.is_active
            ? "admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
            : "admin-teal-modal admin-teal-modal-sm"
        }
      />
    </div>
  );
};

export default AdminStaffPage;